import * as React from 'react';
import styled from 'styled-components';

import { Dialog } from '@longline/aqua-ui/services/Dialog';
import { Message } from '@longline/aqua-ui/containers/Message';

import { Pane } from '../Pane';
import { IDriversetImportResult } from '../../util/DriversetExcel';

interface IProps {
  filename: string;
  result: IDriversetImportResult;
  onClose: () => void;
}

/**
 * What an Excel import did, and everything it had to skip or ignore.
 *
 * Shown after every import, not only failed ones: an import that quietly drops
 * a column the user misspelled is worse than one that says so, and a clean run
 * confirming the row count is cheap reassurance.
 */
const DriversetImportReport = (props: IProps) => {
  const { result } = props;
  const clean = !result.fatal && result.issues.length === 0;

  return (
    <Dialog width={700} inverted open onClose={props.onClose}>
      <Dialog.Header>Import report</Dialog.Header>
      <Dialog.Content>
        <Filename>{props.filename}</Filename>

        {result.fatal
          ? <Message.Error margin>Nothing was imported. The driver set is unchanged.</Message.Error>
          : <Summary>
              Imported <strong>{result.dates.length}</strong> {result.dates.length === 1 ? 'day' : 'days'},
              replacing what was in the editor.
              {result.issues.length > 0 && <> {result.issues.length} {result.issues.length === 1 ? 'problem was' : 'problems were'} found.</>}
            </Summary>
        }

        {clean && <Summary>No problems found.</Summary>}

        {result.issues.length > 0 &&
          <Issues>
            {result.issues.map((issue, idx) => <li key={idx}>{issue.message}</li>)}
          </Issues>
        }
      </Dialog.Content>
      <Dialog.Footer align="end">
        <Pane.CancelButton onClick={props.onClose}>CLOSE</Pane.CancelButton>
      </Dialog.Footer>
    </Dialog>
  )
}

const Filename = styled.div`
  font: ${p => p.theme.font.labelMedium};
  color: ${p => p.theme.colors.neutral[50]};
  margin-bottom: 8px;
  word-break: break-all;
`

const Summary = styled.div`
  font: ${p => p.theme.font.bodyMedium};
  margin-bottom: 12px;
`

const Issues = styled.ul`
  max-height: 320px;
  overflow-y: auto;
  margin: 0;
  padding-left: 20px;

  li {
    font: ${p => p.theme.font.bodySmall};
    margin-bottom: 6px;
  }
`

export { DriversetImportReport }
